import { Button, Modal, Spinner } from '@nike/eds'
import { useRetriggerRasnEventsMutation } from 'api/rasn'
import { useEffect, useState } from 'react'
import { RasnEventType } from 'types'

import './RetriggerRasnEventsModal.css'

interface RetriggerRasnEventsModalProps {
  showModal: boolean
  setShowModal: (showModal: boolean) => void
  rasnNumber: string
}

interface RetriggerStatus {
  isLoading: boolean
  isSuccess: boolean
  isError: boolean
}
const initialRetriggerStatus: RetriggerStatus = {
  isLoading: false,
  isSuccess: false,
  isError: false
}
const defaultStatuses = {
  [RasnEventType.CLOSED]: { ...initialRetriggerStatus },
  [RasnEventType.FULL_RECEIPT]: { ...initialRetriggerStatus }
}

export const RetriggerRasnEventsModal = ({ showModal, setShowModal, rasnNumber }: RetriggerRasnEventsModalProps) => {
  const [retriggerRasnEvents] = useRetriggerRasnEventsMutation()
  const [eventStatuses, setEventStatuses] = useState<Record<string, RetriggerStatus>>(defaultStatuses)

  // Reset on open
  useEffect(() => {
    if (showModal && rasnNumber) {
      setEventStatuses(defaultStatuses)
    }
  }, [showModal, rasnNumber])

  const updateEventStatus = (eventType: RasnEventType, status: Partial<RetriggerStatus>) => {
    setEventStatuses(prev => ({
      ...prev,
      [eventType]: { ...prev[eventType], ...status }
    }))
  }

  const handleRetrigger = (eventType: RasnEventType) => {
    updateEventStatus(eventType, { isLoading: true, isSuccess: false, isError: false })

    retriggerRasnEvents({ rasnNumber, eventType })
      .then(() => {
        updateEventStatus(eventType, { isLoading: false, isSuccess: true })
      })
      .catch(() => {
        updateEventStatus(eventType, { isLoading: false, isError: true })
      })
  }

  const renderEventStatus = (eventType: RasnEventType) => {
    const { isLoading, isSuccess, isError } = eventStatuses[eventType]

    if (isLoading) return <Spinner size="medium" />
    if (isSuccess) return <span className="eds-color--sev-1">SUCCESS</span>
    if (isError) return <span className="eds-color--sev-4">ERROR</span>

    return null
  }

  // internal component to render each event row
  const RasnEventRow = ({ eventType, label }: { eventType: RasnEventType, label: string }) => {
    return (
      <div className="event-row">
        <span>{label}</span>
        <div className="text-center">
          <Button
            onClick={() => { handleRetrigger(eventType) }}
            disabled={eventStatuses[eventType].isLoading}
            variant="secondary"
            size="small"
          >
            Retrigger
          </Button>
        </div>
        <div className="status">
          {renderEventStatus(eventType)}
        </div>
      </div>
    )
  }

  return (
    <Modal
      isOpen={showModal}
      onDismiss={() => { setShowModal(false) }}
      headerSlot={
        <div className="flex justify-between items-center">
          <h3>Retrigger RASN events {rasnNumber}</h3>
        </div>
      }
      footerSlot={
        <div>
          <Button onClick={() => { setShowModal(false) }}>Close</Button>
        </div>
      }
    >
      <div className="px-2 py-0">
        <p>Select an event to retrigger:</p>

        <RasnEventRow
          eventType={RasnEventType.CLOSED}
          label="Closed event"
        />
        <RasnEventRow
          eventType={RasnEventType.FULL_RECEIPT}
          label="Full Receipt event"
        />
      </div>
    </Modal>
  )
}
